"use client";

import Link from "next/link";
import { ChevronRight, Layers } from "lucide-react";

import { ProductPhotoFallback } from "@/components/ProductPhotoFallback";
import { StoreImage } from "@/components/StoreImage";
import { useCustomerGroup } from "@/context/CustomerContext";
import {
  bundleCardPrice,
  bundleComponentsLine,
  bundleOtherComponentsLine,
  bundlePath,
  bundleSavingHint,
  type BundleCardData
} from "@/lib/bundleStorefront";
import { formatPrice } from "@/lib/pricing";

/**
 * Kolase foto bundel: sampul (1 foto) atau foto komponen (2–4). Tanpa foto
 * sama sekali → logo toko redup (`ProductPhotoFallback`), idiom kartu produk.
 * Pemanggil memberi bingkai ber-`relative` + rasio.
 */
export function BundleCollage({
  images,
  alt,
  sizes,
  priority = false
}: {
  images: readonly string[];
  alt: string;
  sizes: string;
  priority?: boolean;
}) {
  const list = images.slice(0, 4);
  if (!list.length) return <ProductPhotoFallback sizes={sizes} />;
  if (list.length === 1) {
    return (
      <StoreImage src={list[0]} alt={alt} fill sizes={sizes} priority={priority} className="object-cover" />
    );
  }
  // 2 → dua kolom; 3 → satu besar + dua bertumpuk; 4 → kisi 2×2.
  const cell = (src: string, i: number, extra = "") => (
    <div key={`${src}-${i}`} className={`relative overflow-hidden bg-neutral-100 ${extra}`}>
      <StoreImage
        src={src}
        alt={i === 0 ? alt : ""}
        fill
        sizes={sizes}
        priority={priority && i === 0}
        className="object-cover"
      />
    </div>
  );
  if (list.length === 2) {
    return <div className="absolute inset-0 grid grid-cols-2 gap-px">{list.map((s, i) => cell(s, i))}</div>;
  }
  if (list.length === 3) {
    return (
      <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-px">
        {cell(list[0], 0, "row-span-2")}
        {cell(list[1], 1)}
        {cell(list[2], 2)}
      </div>
    );
  }
  return (
    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-px">{list.map((s, i) => cell(s, i))}</div>
  );
}

/** Harga/petunjuk hemat kartu — RINGAN: tanpa memuat varian komponen. */
function useCardPriceLine(card: BundleCardData): { price: string | null; hint: string | null } {
  // Mode harga tetap: harga bundel level pembeli ini + tambahan ukuran yang
  // tak terhindarkan (komponen yang hanya menyisakan varian bertambahan).
  // Mode potongan: "Hemat 10%" / "Hemat Rp 25.000" — selalu dari harga
  // pembeli sendiri, jadi janjinya benar untuk semua level.
  const group = useCustomerGroup();
  const price = bundleCardPrice(card, group);
  return {
    price: price !== null ? formatPrice(price) : null,
    hint: bundleSavingHint(card)
  };
}

/**
 * Kartu bundel — dipakai `/bundling`, seksi "Bundel Hemat" `/promo`, dan blok
 * "Lebih hemat dalam bundel" di halaman produk (`variant="compact"`, dengan
 * `productId` produk yang sedang dilihat supaya barisnya berbunyi
 * "+ Koko Aira"). Harga lengkap (terpisah, hemat, tambahan ukuran) hanya di
 * halaman bundelnya.
 */
export function BundleCard({
  card,
  variant = "grid",
  productId,
  priority = false
}: {
  card: BundleCardData;
  variant?: "grid" | "compact";
  /** Produk yang sedang dilihat (mode `compact`). */
  productId?: string;
  priority?: boolean;
}) {
  const { price, hint } = useCardPriceLine(card);
  const href = bundlePath(card.slug);

  if (variant === "compact") {
    const others = productId ? bundleOtherComponentsLine(card, productId) : bundleComponentsLine(card);
    return (
      <Link
        href={href}
        prefetch={false}
        className="flex items-center gap-3 rounded-xl border border-shopee/20 bg-shopeeSoft/40 p-2 transition active:scale-[0.99]"
      >
        <div className="relative aspect-square w-14 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
          <BundleCollage images={card.images} alt={card.name} sizes="56px" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-extrabold text-ink">{card.name}</p>
          {others ? <p className="line-clamp-1 text-[11px] font-semibold text-muted">{others}</p> : null}
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] font-bold text-shopee">
            {price ? <span>{price}</span> : null}
            {hint ? <span>{hint}</span> : null}
            <span className="text-muted">· lihat bundling</span>
          </p>
        </div>
        <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <Link
      href={href}
      prefetch={false}
      className="block overflow-hidden rounded-md bg-white shadow-sm transition active:scale-[0.99]"
    >
      <div className="relative aspect-square bg-neutral-100">
        <BundleCollage images={card.images} alt={card.name} sizes="(max-width: 448px) 50vw, 224px" priority={priority} />
        <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded bg-shopee/95 px-1.5 py-0.5 text-[10px] font-extrabold text-white">
          <Layers size={11} aria-hidden="true" />
          Bundling {card.components.length} barang
        </span>
        {card.unavailable ? (
          <span className="absolute inset-x-0 bottom-0 bg-zinc-900/75 px-2 py-1 text-center text-[10px] font-bold text-white">
            Tidak tersedia
          </span>
        ) : null}
      </div>
      <div className="space-y-1 p-2">
        <p className="line-clamp-2 min-h-8 text-xs font-bold leading-4 text-ink">{card.name}</p>
        <p className="line-clamp-2 text-[10px] font-semibold leading-4 text-muted">
          {bundleComponentsLine(card)}
        </p>
        {card.unavailable ? (
          <p className="line-clamp-2 text-[10px] font-semibold text-muted">{card.unavailable}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-1">
            {price ? <p className="text-sm font-bold text-shopee">{price}</p> : null}
            {hint ? (
              <span className="shrink-0 rounded bg-shopeeSoft px-1 py-0.5 text-[10px] font-bold text-shopee">
                {hint}
              </span>
            ) : null}
            {!price && !hint ? (
              <p className="text-[11px] font-bold text-shopee">Lihat harga bundling</p>
            ) : null}
          </div>
        )}
      </div>
    </Link>
  );
}
